import { useState, useEffect } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, isCardTransparent, hexToRgba, resolveVideo, buildSrcSet, getImageSizes } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIconInline from './SectionIcon';

export default function ProgramsSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const items = content.items || [];
    const variant = config.variant || 'accordion';
    const accent = config.accent_color || primaryColor;
    const [openIndex, setOpenIndex] = useState(0);
    const [videoModal, setVideoModal] = useState(null);
    const [carouselIdxs, setCarouselIdxs] = useState({});

    // Auto-advance carousels (grid variant)
    useEffect(() => {
        if (variant !== 'grid') return;
        const carouselItems = items.map((it, i) => {
            if (it.media_type !== 'carousel') return null;
            const imgs = it.carousel_folder_id && mediaFolders[it.carousel_folder_id]
                ? (it.carousel_selected_file_ids?.length > 0 ? mediaFolders[it.carousel_folder_id].filter(f => it.carousel_selected_file_ids.includes(f.id)) : mediaFolders[it.carousel_folder_id])
                : [];
            return imgs.length > 1 ? { idx: i, len: imgs.length } : null;
        }).filter(Boolean);
        if (carouselItems.length === 0) return;
        const t = setInterval(() => {
            setCarouselIdxs(prev => {
                const next = {...prev};
                carouselItems.forEach(c => { next[c.idx] = ((prev[c.idx] || 0) + 1) % c.len; });
                return next;
            });
        }, 4000);
        return () => clearInterval(t);
    }, [variant, items, mediaFolders]);

    // Render icon: supports both emoji strings and icon-picker names
    const isEmoji = (str) => /^[\p{Emoji}\u200d\ufe0f]+$/u.test(str);
    const ItemIcon = ({ icon, size = 24, className = '' }) => {
        if (!icon) return null;
        if (isEmoji(icon)) return <span className={className} style={{ fontSize: size }}>{icon}</span>;
        return <SectionIconInline name={icon} size={size} className={className} />;
    };

    if (items.length === 0) return null;

    const progColumns = config.columns || 2;
    const progColClass = progColumns === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : progColumns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const itemBg = s.dark ? 'bg-white/5' : 'bg-white';
    const itemBorder = s.dark ? 'border-white/10' : 'border-gray-100';

    // Shared: item detail row
    const DetailRow = ({ icon, text }) => text ? (
        <div className="flex items-center gap-2 text-xs" style={{ color: s.textSub }}>
            <svg className="w-3.5 h-3.5 shrink-0" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon}/></svg>
            {text}
        </div>
    ) : null;
    const clockIcon = "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z";
    const calendarIcon = "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z";
    const userIcon = "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z";
    const card = getCardClass(cardStyle, s.dark);

    // ── Cards variant ──
    if (variant === 'cards') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${progColClass} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i} className={`group ${card} rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {/* Image or color bar */}
                                {item.image_url ? (
                                    <div className="h-44 overflow-hidden">
                                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                                    </div>
                                ) : (
                                    <div className="h-2 transition-all duration-300 group-hover:h-3" style={{ backgroundColor: s.accent }}/>
                                )}
                                <div className="p-6 flex flex-col h-full">
                                    {(item.age_range || item.duration) && (
                                        <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3 self-start" style={{backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent}}>
                                            {item.age_range || item.duration}
                                        </span>
                                    )}
                                    <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm leading-relaxed mb-4" style={{ color: s.textSub }}>{item.description}</p>}
                                    <div className="space-y-1.5 mb-4">
                                        <DetailRow icon={clockIcon} text={item.hours} />
                                        <DetailRow icon={calendarIcon} text={item.requirements} />
                                    </div>
                                    {item.price && (
                                        <div className="pt-4 mt-auto border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                                            <span className="text-xl font-bold" style={{ color: s.accent }}>{item.price}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // ── Grid + media ──
    if (variant === 'grid') {
        // Resolve image from mediaFolders
        const resolveImage = (folderId, fileId, fallbackUrl) => {
            if (folderId && fileId && mediaFolders[folderId]) {
                const f = mediaFolders[folderId].find(x => String(x.id) === String(fileId));
                if (f) return { url: f.image_url, file: f };
            }
            return { url: fallbackUrl || null, file: null };
        };
        const resolveCarouselImages = (item) => {
            if (item.carousel_folder_id && mediaFolders[item.carousel_folder_id]) {
                const all = mediaFolders[item.carousel_folder_id] || [];
                const sel = item.carousel_selected_file_ids;
                return (sel?.length > 0 ? all.filter(f => sel.includes(f.id)) : all).map(f => ({ url: f.image_url, file: f }));
            }
            return (item.carousel_images || []).map(url => ({ url, file: null }));
        };

        const ItemMedia = ({ item, idx }) => {
            const mt = item.media_type || (item.image_url ? 'image' : 'icon');
            if (mt === 'video') {
                const vid = resolveVideo(item, 'video', mediaFolders);
                if (vid?.type === 'file') return <video src={vid.src} className="w-full h-full object-cover" controls preload="metadata" />;
                const thumbData = resolveImage(item.video_thumb_folder_id, item.video_thumb_file_id, item.video_thumb);
                const thumb = thumbData.url || vid?.thumb;
                return (
                    <div className="relative w-full h-full cursor-pointer group/vid" onClick={() => vid && setVideoModal(vid)}>
                        {thumb ? <img src={thumb} alt="" srcSet={buildSrcSet(thumbData.file)} sizes={getImageSizes(progColumns)} className="w-full h-full object-cover group-hover/vid:scale-105 transition-transform duration-500" decoding="async" /> : <div className="w-full h-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }} />}
                        <div className="absolute inset-0 bg-black/10 group-hover/vid:bg-black/30 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-transform group-hover/vid:scale-110" style={{ backgroundColor: hexToRgba(s.accent, 0.85) }}>
                                <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                            </div>
                        </div>
                    </div>
                );
            }
            if (mt === 'carousel') {
                const imgs = resolveCarouselImages(item);
                if (imgs.length === 0) return <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}><span className="text-xs text-gray-400">Sin imagenes</span></div>;
                const ci = (carouselIdxs[idx] || 0) % imgs.length;
                return (
                    <div className="relative w-full h-full">
                        {imgs.map((img, j) => (
                            <img key={j} src={img.url} alt="" srcSet={buildSrcSet(img.file)} sizes={getImageSizes(progColumns)} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${j === ci ? 'opacity-100' : 'opacity-0'}`} loading="lazy" decoding="async" />
                        ))}
                        {imgs.length > 1 && (
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                                {imgs.map((_, j) => <span key={j} className={`w-1.5 h-1.5 rounded-full transition-all ${j === ci ? 'bg-white scale-125' : 'bg-white/50'}`} />)}
                            </div>
                        )}
                    </div>
                );
            }
            if (mt === 'image') {
                const imgData = resolveImage(item.image_folder_id, item.image_file_id, item.image_url);
                if (imgData.url) return <img src={imgData.url} alt={item.title} srcSet={buildSrcSet(imgData.file)} sizes={getImageSizes(progColumns)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" decoding="async" />;
            }
            // Icon fallback
            return (
                <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                        <ItemIcon icon={item.icon || '📋'} size={32} />
                    </div>
                </div>
            );
        };

        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${progColClass} gap-8`}>
                        {items.map((item, i) => (
                            <div key={i} className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                                <div className="h-52 overflow-hidden">
                                    <ItemMedia item={item} idx={i} />
                                </div>
                                {(item.age_range || item.duration) && (
                                    <span className="absolute top-4 left-4 text-xs font-bold px-3 py-1 rounded-full text-white shadow-lg" style={{ backgroundColor: s.accent }}>
                                        {item.age_range || item.duration}
                                    </span>
                                )}
                                <div className={`p-6 ${s.dark ? 'bg-white/5' : 'bg-white border border-gray-100'} rounded-b-2xl`}>
                                    <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                    {item.description && <p className="text-sm leading-relaxed mb-3 line-clamp-3" style={{ color: s.textSub }}>{item.description}</p>}
                                    <div className="flex items-center justify-between">
                                        <div className="space-y-1">
                                            <DetailRow icon={clockIcon} text={item.hours} />
                                            <DetailRow icon={calendarIcon} text={item.requirements} />
                                        </div>
                                        {item.price && <span className="text-lg font-bold" style={{ color: s.accent }}>{item.price}</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
                {/* Video modal */}
                {videoModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8" onClick={() => setVideoModal(null)}>
                        <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setVideoModal(null)} className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                                {videoModal.type === 'file'
                                    ? <video src={videoModal.src} className="absolute inset-0 w-full h-full" controls autoPlay />
                                    : <iframe src={`${videoModal.embed}?autoplay=1&rel=0`} className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />}
                            </div>
                        </div>
                    </div>
                )}
            </section>
        );
    }

    // ── Lista numerada ──
    if (variant === 'list') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="max-w-4xl mx-auto space-y-6">
                        {items.map((item, i) => (
                            <div key={i} className={`flex gap-6 items-start ${card} rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                {/* Number */}
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-xl font-black" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-4 mb-1">
                                        <h3 className="text-lg font-bold" style={{ color: s.text }}>{item.title}</h3>
                                        {item.price && <span className="text-lg font-bold shrink-0" style={{ color: s.accent }}>{item.price}</span>}
                                    </div>
                                    {(item.age_range || item.duration) && (
                                        <span className="inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full mb-2" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>
                                            {item.age_range || item.duration}
                                        </span>
                                    )}
                                    {item.description && <p className="text-sm leading-relaxed mb-2" style={{ color: s.textSub }}>{item.description}</p>}
                                    <div className="flex flex-wrap gap-4">
                                        <DetailRow icon={clockIcon} text={item.hours} />
                                        <DetailRow icon={calendarIcon} text={item.requirements} />
                                    </div>
                                </div>
                                {/* Image thumbnail */}
                                {item.image_url && (
                                    <div className="hidden md:block w-28 h-20 rounded-xl overflow-hidden shrink-0">
                                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // ── Tabs ──
    if (variant === 'tabs') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    {/* Tab buttons */}
                    <div className="flex flex-wrap gap-2 mb-8 justify-center">
                        {items.map((item, i) => (
                            <button key={i} onClick={() => setOpenIndex(i)}
                                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${openIndex === i ? 'shadow-lg scale-105' : 'hover:opacity-80'}`}
                                style={openIndex === i
                                    ? { backgroundColor: s.accent, color: '#ffffff' }
                                    : { backgroundColor: hexToRgba(s.accent, 0.06), color: s.textSub }
                                }>
                                {item.title}
                            </button>
                        ))}
                    </div>
                    {/* Active tab content */}
                    {items[openIndex] && (() => {
                        const item = items[openIndex];
                        return (
                            <div className={`${card} rounded-2xl overflow-hidden transition-all duration-500`}>
                                <div className={`grid grid-cols-1 ${item.image_url ? 'lg:grid-cols-2' : ''}`}>
                                    {item.image_url && (
                                        <div className="h-64 lg:h-auto overflow-hidden">
                                            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                        </div>
                                    )}
                                    <div className="p-8 flex flex-col justify-center">
                                        <div className="flex items-center gap-3 mb-4">
                                            <h3 className="text-2xl font-bold" style={{ color: s.text }}>{item.title}</h3>
                                            {(item.age_range || item.duration) && (
                                                <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: hexToRgba(s.accent, 0.08), color: s.accent }}>
                                                    {item.age_range || item.duration}
                                                </span>
                                            )}
                                        </div>
                                        {item.description && <p className="text-base leading-relaxed mb-5" style={{ color: s.textSub }}>{item.description}</p>}
                                        <div className="flex flex-wrap gap-6 mb-4">
                                            <DetailRow icon={clockIcon} text={item.hours} />
                                            <DetailRow icon={calendarIcon} text={item.requirements} />
                                        </div>
                                        {item.price && (
                                            <div className="pt-4 border-t" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                                                <span className="text-2xl font-bold" style={{ color: s.accent }}>{item.price}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // ── Accordion variant (default) ──
    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div className="max-w-3xl mx-auto space-y-3">
                    {items.map((item, i) => {
                        const isOpen = openIndex === i;
                        return (
                            <div key={i}
                                className={`rounded-xl border transition-all duration-300 ${itemBg} ${isOpen ? `${s.dark ? 'border-white/20' : 'border-gray-200'} shadow-sm` : itemBorder} ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                                <button
                                    onClick={() => setOpenIndex(isOpen ? -1 : i)}
                                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                                >
                                    <div className="flex items-center gap-3">
                                        {item.icon && <ItemIcon icon={item.icon} size={20} />}
                                        <div>
                                            <h4 className="text-base font-bold" style={{ color: s.text }}>{item.title}</h4>
                                            {item.duration && <span className="text-xs font-medium" style={{ color: accent }}>{item.duration}</span>}
                                        </div>
                                    </div>
                                    <svg className={`w-5 h-5 shrink-0 ml-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                        style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <div className="px-6 pb-5 space-y-4">
                                        <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {item.requirements && (
                                                <div className="rounded-lg p-4" style={{ backgroundColor: hexToRgba(accent, 0.03) }}>
                                                    <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: accent }}>Requisitos</h5>
                                                    <p className="text-sm" style={{ color: s.textSub }}>{item.requirements}</p>
                                                </div>
                                            )}
                                            {item.price && (
                                                <div className="rounded-lg p-4" style={{ backgroundColor: hexToRgba(accent, 0.03) }}>
                                                    <h5 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: accent }}>Inversion</h5>
                                                    <p className="text-lg font-bold" style={{ color: s.text }}>{item.price}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}
